/** Default / reset width — matches `:root` `--sidebar-width` (14.25rem at 16px). */
export const SIDEBAR_WIDTH_DEFAULT_PX = 228
export const SIDEBAR_WIDTH_MIN_PX = 170
export const SIDEBAR_WIDTH_MAX_PX = 340
/** Collapsed icon rail — matches `--sidebar-width-icon` / `--sidebar-width-collapsed` (3rem). */
export const SIDEBAR_WIDTH_ICON_PX = 48
/**
 * Mouse-up collapse threshold. Same idea as VS Code’s sash snap: stay labelled
 * until min, then require about half the labelled min past that floor (~85px)
 * before committing to the icon rail. Release between snap and min springs
 * back to 170.
 */
export const SIDEBAR_WIDTH_SNAP_PX =
  SIDEBAR_WIDTH_MIN_PX - Math.floor(SIDEBAR_WIDTH_MIN_PX / 2)
/** Matches sidebar width CSS (`duration-200`). */
export const SIDEBAR_WIDTH_SNAP_DURATION_MS = 200
export const SIDEBAR_WIDTH_STEP_PX = 8
export const SIDEBAR_WIDTH_LARGE_STEP_PX = 32
export const SIDEBAR_WIDTH_STORAGE_KEY = "sidebar-width"

export type SidebarDragResult =
  | { open: true; width: number }
  | { open: false }

export function clampSidebarWidth(px: number): number {
  return Math.min(SIDEBAR_WIDTH_MAX_PX, Math.max(SIDEBAR_WIDTH_MIN_PX, Math.round(px)))
}

/** Live drag range: icon rail through max (labels hide below min). */
export function clampSidebarLiveWidth(px: number): number {
  return Math.min(SIDEBAR_WIDTH_MAX_PX, Math.max(SIDEBAR_WIDTH_ICON_PX, Math.round(px)))
}

export function parseStoredSidebarWidth(raw: string | null | undefined): number {
  if (raw == null || raw === "") return SIDEBAR_WIDTH_DEFAULT_PX
  const parsed = Number(raw)
  if (!Number.isFinite(parsed)) return SIDEBAR_WIDTH_DEFAULT_PX
  return clampSidebarWidth(parsed)
}

export function readStoredSidebarWidth(): number {
  if (typeof window === "undefined") return SIDEBAR_WIDTH_DEFAULT_PX
  try {
    return parseStoredSidebarWidth(window.localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY))
  } catch {
    return SIDEBAR_WIDTH_DEFAULT_PX
  }
}

export function persistSidebarWidth(px: number): void {
  if (typeof window === "undefined") return
  try {
    window.localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(clampSidebarWidth(px)))
  } catch {
    // quota / private mode
  }
}

/** Keyboard / discrete steps: collapse as soon as you leave the labelled min. */
export function resolveSidebarDrag(pointerWidth: number): SidebarDragResult {
  if (pointerWidth < SIDEBAR_WIDTH_MIN_PX) return { open: false }
  return { open: true, width: clampSidebarWidth(pointerWidth) }
}

/**
 * Pointer-up after a drag. Uses snap hysteresis so a tiny dip below min
 * does not collapse — the edge springs back to 170 instead.
 */
export function resolveSidebarDragRelease(pointerWidth: number): SidebarDragResult {
  if (pointerWidth < SIDEBAR_WIDTH_SNAP_PX) return { open: false }
  return { open: true, width: clampSidebarWidth(pointerWidth) }
}

/**
 * Live drag preview: never collapse mid-gesture. Below min while already
 * open, follow the pointer down to the icon rail (icon chrome, not labels).
 * While collapsed, stay collapsed until the pointer leaves the icon rail.
 */
export function previewSidebarDrag(
  pointerWidth: number,
  currentlyOpen: boolean
): SidebarDragResult {
  if (!currentlyOpen && pointerWidth <= SIDEBAR_WIDTH_ICON_PX) return { open: false }
  return { open: true, width: clampSidebarLiveWidth(pointerWidth) }
}

export function stepSidebarWidth(width: number, delta: number): SidebarDragResult {
  return resolveSidebarDrag(width + delta)
}
